import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Drawer, Button, notification } from 'antd';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../../api/useAPIMutation';
import Form from '../../components/Form';
import PageTitle from '../../components/PageTitle';
import UserForm, { UserFormValues } from '../../components/UserForm';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import styles from './CreateUser.module.css';

const CreateUser = () => {
  const { t } = useTranslation();

  const [form] = Form.useForm<UserFormValues>();
  const queryClient = useQueryClient();

  const { tenant } = useTypedParams({ tenant: String });
  const navigate = useTypedNavigate();

  const mutation = useAPIMutation(
    async (variables: UserFormValues, api) => {
      const user = await api.post(`/users/`, {
        username: variables.username || '',
        full_name: variables.name || '',
        email: variables.email,
        groups: variables.groups
          ? Array.isArray(variables.groups)
            ? variables.groups
            : [variables.groups]
          : [],
      });

      return user;
    },
    {
      onSuccess: (response) => {
        notification.success({
          message: t('Usuário criado com sucesso!'),
          description: t(
            'Enviamos um email para o novo usuário com as instruções de primeiro acesso.'
          ),
          duration: 0,
        });
        navigate({
          pathname: '/:tenant/settings/cs-staff/users/:user_id/',
          params: { tenant, user_id: response.data.id },
        });
      },
      onError: (err, variables, context) => {
        // TODO: POG TEMPORARIO ARRUMAR DPS
        if (
          err?.response?.data?.email?.[0] === 'usuário com este Endereço de e-mail já existe.' ||
          err?.response?.data?.email?.[0] === 'user with this Email address already exists.'
        ) {
          return notification.error({
            duration: 0,
            message: t('Atenção'),
            description: t('Já existe um usuário com este endereço de E-mail cadastrado.'),
          });
        }
        if (
          // TODO: VER SE TRADUZ AQ
          err?.response?.data?.username?.[0] === 'Um usuário com este nome de usuário já existe.'
        ) {
          return notification.error({
            duration: 0,
            message: t('Atenção'),
            description: t('Já existe uma pessoa com esse nome de Usuário cadastrado.'),
          });
        }

        return queryClient.defaultMutationOptions().onError?.(err, variables, context);
      },
      onSettled: () => {
        queryClient.invalidateQueries('/users/');
      },
    }
  );

  const onClose = () =>
    navigate({ pathname: '/:tenant/settings/cs-staff/users/', params: { tenant } });

  return (
    <Drawer
      className={styles.drawer}
      title={
        <PageTitle
          title={t('Novo usuário')}
          level={2}
          extra={[
            <Button key="close" type="text" onClick={onClose} shape="circle">
              <CloseOutlined />
            </Button>,
          ]}
        />
      }
      maskClosable
      size="large"
      closable={false}
      width={720}
      footer={
        <Button
          type="primary"
          htmlType="submit"
          icon={<PlusOutlined />}
          loading={mutation.isLoading}
          onClick={() => form.submit()}
        >
          <Trans>Adicionar</Trans>
        </Button>
      }
      open
      onClose={onClose}
    >
      <Helmet>
        <title>
          {t('Novo usuário')} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <UserForm
        form={form}
        layout="vertical"
        onFinish={(variables) => {
          mutation.mutate(variables);
        }}
      />
    </Drawer>
  );
};

export default withRouteChange()(CreateUser);
